import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  Container,
  Button,
  Message,
  Modal,
  Popup,
  Label
} from 'semantic-ui-react';
import { toast } from 'react-toastify';
import errors from './constants/errors';

import { makeGetWithParam } from './../../../../src/services/ServiceManager';
import {
  insertTag,
  getTagsForCategories
} from './../../../../src/services/TagService';

class AssetTagSelection extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      tagFields: [
        {
          key: 'oem',
          placeholder: 'OEM'
        },
        {
          key: 'type',
          placeholder: 'Asset Type'
        },
        {
          key: 'year',
          placeholder: 'Year'
        },
        {
          key: 'account',
          placeholder: 'Account'
        },
        {
          key: 'product',
          placeholder: 'Model/Product'
        },
        {
          key: 'language',
          placeholder: 'Language'
        },
        {
          key: 'corporate',
          placeholder: 'Corporate Target'
        },
        {
          key: 'salesEvent',
          placeholder: 'Sales Event'
        }
      ],
      selectedTagOptions: {
        /**
         * {
         *    category : [tag.id]
         * }
         */
      },
      loadingCategory: null, // null indicates it is not being loaded currently, otherwise, this value will be the category name.
      updating: false,
      error: {
        type: null, // 'UPDATE_TAG_FAIL', 'NO_ASSET_SELECTED', 'TAG_UPDATE_FAILED'
        message: ''
      },
      showConfirmModal: false,
      confirmNewTagAdd: false,
      /**
       * {
       *    category, value, options
       * }
       */
      newTagInfo: null, // tag info that user newly add
      searchTagPlaceholders: {}
    };
  }

  categories = ['account', 'language', 'corporate', 'salesEvent'];

  componentDidMount() {
    const { containerComponent } = this.props;
    if (containerComponent === 'FILTER_PANEL') {
      this.getPlaceholderFromSession();
    }
  }

  onDropdownClick = (event, data) => {
    // check if the selection category for the dropdown already loaded data.
    const { category } = data;
    const { tagOptions, user } = this.props;
    const { groups, email } = user;
    const applyGuestFilter = ['NU'].includes(groups[0]);

    if (!category || !tagOptions) return;

    const categories = Object.keys(tagOptions);

    const isTagOptionsLoaded = categories.indexOf(category) > -1;

    if (!isTagOptionsLoaded) {
      this.setState(
        {
          ...this.state,
          loadingCategory: category
        },
        () => {
          // TODO: check when editing the url in the function. On server the request may fail.
          makeGetWithParam(
            `service/tags/category/${category}`,
            null,
            `${
              applyGuestFilter ? `?created_by=${encodeURIComponent(email)}` : ''
            }`
          ).then((result) => {
            const { tags } = result || {};

            if (!tags) return; // the user needs to click it again to re-try.

            const selected = tags.map((tag) => {
              const { id, value } = tag;
              return {
                key: id,
                text: value,
                value: id,
                category
              };
            });

            const { updateTagOptions } = this.props;

            if (!updateTagOptions) return;

            updateTagOptions(
              {
                [category]: selected
              },
              // this callback will be called when the tags selected are set for this category in AssetGallery.jsx
              () => {
                this.setState((prevState) => ({
                  ...prevState,
                  loadingCategory: null
                }));
              }
            );
          });
        }
      );
    }
  };

  onTagChange = (event, data) => {
    const { category: selectedCategory, value, multiple, options } = data;
    const optionKeys = options.map((option) => option.key);

    // if new tag has been added, onTagChange() will be called first and then onAddTag()
    // skip this function if new tag has been added
    // NOTE when user deleted the last selection option from multiple selection dropdown, the value === [];
    // Need to update the selectedOptions
    if (
      !(value instanceof Array) ||
      (value instanceof Array && value.length > 0)
    ) {
      let isOptionExist = false;
      if (multiple) {
        // for multiple selection, check if all values are in the options.
        // for "product" field, it can have the values in options and that are not if user adding new tag when at least one tag is selected before.
        isOptionExist =
          value.filter((val) => optionKeys.indexOf(val) > -1).length ===
          value.length;
      } else {
        isOptionExist = optionKeys.indexOf(value) > -1;
      }

      if (!isOptionExist) {
        this.setState((prevState) => ({
          ...prevState,
          showConfirmModal: true,
          confirmNewTagAdd: false,
          newTagInfo: {
            category: selectedCategory,
            value: multiple
              ? `${value.filter((tmp) => optionKeys.indexOf(tmp) === -1)}`
              : value,
            options
          }
        }));
        return; // new tag has been added
      }
    }

    let selectedTagOptions = [];

    if (multiple) {
      selectedTagOptions = value.map((selectedId) => {
        const option = options.find((item) => item.key === selectedId); // options[i].key is the tag id.

        if (!option) {
          throw new Error(
            'The selected Tag id cannot be found in the options.'
          );
        }

        return option;
      });
    } else {
      const selectedTagOption = options.find((option) => option.key === value);

      if (!selectedTagOption) {
        throw new Error('The selected Tag id cannot be found in the options.');
      }

      selectedTagOptions = [selectedTagOption];
    }

    this.setState(
      {
        ...this.state,
        selectedTagOptions: {
          ...this.state.selectedTagOptions,
          [selectedCategory]: selectedTagOptions
        },
        searchTagPlaceholders: {}
      },
      () => {
        const { updateSelectedTagOptions } = this.props;

        if (updateSelectedTagOptions) {
          updateSelectedTagOptions(selectedCategory, selectedTagOptions);
          this.saveSearchSession();
        }
      }
    );
  };

  onAddTag = (event, { category, value, options }) => {
    const { allowAdditions } = this.props;

    if (!allowAdditions) return; // add tag not allowed.

    this.addTagOnceConfirmed({ category, value, options });
  };

  addTagOnceConfirmed = ({ category, value, options }) => {
    const { confirmNewTagAdd } = this.state;

    if (!confirmNewTagAdd) return;

    const { user, displayError } = this.props;

    const { email } = user || {};

    if (!user || !email || email === '') {
      displayError({
        type: errors.ASSET_GALLERY.INVALID_USER,
        title: 'Invalid User Info.',
        message: 'The user email address is not valid.'
      });

      return;
    }

    const isRequired =
      ['oem', 'type', 'year', 'product', 'language', 'condition'].indexOf(
        category
      ) > -1;

    const tag = {
      category,
      required: isRequired,
      value,
      created_by: email
    };

    insertTag(tag).then((result) => {
      const { statusCode, tag: addedTag } = result;

      // TODO: We need to handle the case when it failed.
      if (statusCode === 200) {
        const { id } = addedTag || {};

        const newTagOption = {
          key: id,
          text: value,
          value: id,
          category
        };

        const {
          updateTagOptions,
          updateSelectedTagOptions,
          onTagAdd,
          selectedTagOptions
        } = this.props;

        if (!updateTagOptions) return;

        this.setState(
          {
            ...this.state,
            confirmNewTagAdd: false,
            newTagInfo: null
          },
          () => {
            updateTagOptions({ [category]: [...options, newTagOption] }, () => {
              if (updateSelectedTagOptions) {
                const existingSelectedOptions = selectedTagOptions[category]
                  ? selectedTagOptions[category]
                  : [];
                updateSelectedTagOptions(category, [
                  ...existingSelectedOptions,
                  newTagOption
                ]);
              }
              if (onTagAdd) onTagAdd(category);
            });
          }
        );
      }
    });
  };

  onConfirmAddTag = (event) => {
    event.preventDefault();

    this.setState(
      {
        ...this.state,
        showConfirmModal: false,
        confirmNewTagAdd: true
      },
      () => {
        const { newTagInfo } = this.state;

        if (!newTagInfo) return;

        const { category, value, options } = newTagInfo;

        this.addTagOnceConfirmed({ category, value, options });
      }
    );
  };

  onRejectAddTag = (event) => {
    event.preventDefault();

    this.setState((prevState) => ({
      ...prevState,
      showConfirmModal: false,
      confirmNewTagAdd: false,
      newTagInfo: null
    }));
  };

  onTagUpdateButtonClick = (event) => {
    event.preventDefault();

    const { editing, selectedAssets } = this.props;

    if (editing && selectedAssets.length === 0) {
      this.setState((prevState) => ({
        ...prevState,
        error: {
          type: errors.ASSET_TAG_SELECTION.NO_ASSET_SELECTED,
          message: 'No asset was selected.'
        }
      }));

      return;
    }

    if (editing && selectedAssets.length > 0) {
      this.setState(
        {
          ...this.state,
          updating: true,
          error: {
            type: null,
            message: ''
          }
        },
        () => {
          const { updateAssetTags } = this.props;

          if (!updateAssetTags) return;

          updateAssetTags((error) => {
            // error should be same structure as the one in the state.
            this.setState((prevState) => ({
              ...prevState,
              updating: false,
              error
            }));
          });
        }
      );
    } else {
      // single asset tag update
      this.setState(
        {
          ...this.state,
          updating: true,
          error: {
            type: null,
            message: ''
          }
        },
        () => {
          const { updateAssetTags } = this.props;

          if (!updateAssetTags) return;

          // result is the response returned from service/tags PUT.
          // This callback will be called only if there is an error while updating the tags.
          updateAssetTags((error) => {
            const { type } = error || {};

            if (!type) return;

            this.setState((prevState) => ({
              ...prevState,
              updating: false,
              error
            }));
          });
        }
      );
    }
  };

  onResetTagClick = (event) => {
    event.preventDefault();

    const { resetSelectTagOptions } = this.props;

    if (resetSelectTagOptions) resetSelectTagOptions();
  };

  saveSearchSession = () => {
    const { selectedTagOptions } = this.state;
    window.sessionStorage.setItem(
      'searchParamsFilterPanel',
      JSON.stringify(selectedTagOptions)
    );
  };

  loadSearchSession = () => {
    const searchParams = window.sessionStorage.getItem(
      'searchParamsFilterPanel'
    );
    if (searchParams) {
      return JSON.parse(searchParams);
    }
    return {};
  };

  getPlaceholderFromSession = async () => {
    const searchParams = this.loadSearchSession();
    const foundPlaceHolders = {};
    if (searchParams) {
      const { groups, email } = this.props.user;
      const applyGuestFilter = ['NU'].includes(groups[0]);

      const tagsForCategoriesData = await getTagsForCategories(
        this.categories,
        applyGuestFilter ? email : null
      );
      const tagsForCategories = tagsForCategoriesData
        ? tagsForCategoriesData.results
        : {};
      if (tagsForCategories) {
        tagsForCategories.forEach((catGroup) => {
          const category = catGroup.category;
          if (searchParams[category] && searchParams[category].length >= 1) {
            foundPlaceHolders[category] = [];
            searchParams[category].forEach((tagOption) => {
              const foundTags = catGroup.tags.filter((tag) => {
                return tagOption.value === tag.id;
              });
              if (foundTags.length > 0) {
                foundTags.forEach((foundTag) => {
                  foundPlaceHolders[category].push(foundTag.value);
                });
              }
            });
          }
        });
        if (foundPlaceHolders) {
          this.setState({ searchTagPlaceholders: foundPlaceHolders });
        }
      }
    }
  };

  setSearchParamsFromSession = async () => {
    const { searchTagPlaceholders } = this.state;
    const searchParams = this.loadSearchSession();

    if (searchTagPlaceholders) {
      toast.info('Last searched tags were applied!', {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      const { updateSelectedTagOptions, user } = this.props;
      const { groups, email } = user;
      const applyGuestFilter = ['NU'].includes(groups[0]);

      await Promise.all(
        this.categories.map(async (category) => {
          if (searchParams[category]) {
            if (
              searchParams[category] !== [] &&
              searchParams[category].length >= 1
            ) {
              if (updateSelectedTagOptions) {
                this.updateSelectedTagOptions(
                  category,
                  searchParams[category],
                  applyGuestFilter ? email : null
                );
              }
            }
          }
        })
      );

      this.setState({
        selectedTagOptions: searchParams,
        searchTagPlaceholders: {}
      });
    }
  };

  updateSelectedTagOptions = (category, categoryTags, email = null) => {
    makeGetWithParam(
      `service/tags/category/${category}`,
      null,
      `${email ? `?created_by=${encodeURIComponent(email)}` : ''}`
    ).then((result) => {
      const { tags } = result;

      const selected = tags.map((tag) => {
        const { id, value } = tag;
        return {
          key: id,
          text: value,
          value: id,
          category
        };
      });

      const { updateTagOptions } = this.props;

      updateTagOptions(
        {
          [category]: selected
        },
        // this callback will be called when the tags selected are set for this category in AssetGallery.jsx
        () => {
          const { updateSelectedTagOptions } = this.props;
          updateSelectedTagOptions(category, categoryTags);
        }
      );
    });
  };

  render() {
    const {
      tagOptions,
      allowAdditions,
      style,
      ableToUpdateTags,
      containerComponent,
      selectedTagOptions,
      highLightRequiredFields,
      requiredTagFields,
      loadingOptions
    } = this.props;
    const {
      tagFields,
      loadingCategory,
      updating,
      error,
      showConfirmModal,
      newTagInfo,
      searchTagPlaceholders
    } = this.state;

    const selectedTagKeys = Object.keys(selectedTagOptions);

    return (
      <Container style={{ ...(style || {}) }}>
        {containerComponent === 'FILTER_PANEL' ? (
          <Label
            as="a"
            corner="left"
            icon="magic"
            onClick={() => this.setSearchParamsFromSession()}
            hideOnScroll
          />
        ) : null}
        {tagFields
          .filter((tagField) => {
            // This AssetTagSelection.jsx is being used in FilterPanel.jsx and UploaderPanel.jsx
            // In FilterPanel.jsx, the tag search fields (oem and type) are placed on the AssetGalleryNav.jsx.
            // Exclude these two fields if it is being used in FilterPanel.jsx
            if (containerComponent === 'FILTER_PANEL') {
              const { key } = tagField;

              return (
                key !== 'oem' &&
                key !== 'type' &&
                key !== 'year' &&
                key !== 'product'
              );
            }

            return true;
          })
          .map((tagField) => {
            const { key, placeholder } = tagField;
            const allowMultipleSelection =
              key === 'product' ||
              key === 'object' ||
              containerComponent === 'FILTER_PANEL';

            let options = [];

            if (tagOptions && tagOptions[key]) {
              options = tagOptions[key];
            }

            // TODO: Below dropdown generation code can be shortened.
            let dropdown = null;
            if (allowMultipleSelection) {
              const values =
                selectedTagOptions && selectedTagOptions[key]
                  ? selectedTagOptions[key].map((option) => option.key)
                  : [];

              dropdown = (
                <Dropdown
                  id={key}
                  key={key}
                  category={key}
                  placeholder={
                    searchTagPlaceholders[key]
                      ? searchTagPlaceholders[key].join()
                      : placeholder
                  }
                  options={options}
                  allowAdditions={allowAdditions}
                  loading={loadingCategory === key || loadingOptions}
                  value={values}
                  fluid
                  selection
                  multiple={allowMultipleSelection}
                  search
                  style={
                    highLightRequiredFields &&
                    selectedTagKeys.indexOf(key) === -1 &&
                    requiredTagFields.indexOf(key) !== -1
                      ? { marginBottom: '1em', borderColor: '#f2711c' }
                      : { marginBottom: '1em' }
                  }
                  onClick={this.onDropdownClick}
                  onAddItem={this.onAddTag}
                  onChange={this.onTagChange}
                />
              );
            } else {
              const { [key]: option } = selectedTagOptions || {};

              let value = '';
              if (option) {
                const { value: selectedValue } = option[0]; // this must exists..

                value = selectedValue;
              }

              dropdown = (
                <Dropdown
                  id={key}
                  key={key}
                  category={key}
                  placeholder={
                    searchTagPlaceholders[key]
                      ? searchTagPlaceholders[key].join()
                      : placeholder
                  }
                  options={options}
                  allowAdditions={allowAdditions}
                  loading={loadingCategory === key || loadingOptions}
                  value={value}
                  fluid
                  selection
                  multiple={allowMultipleSelection}
                  search
                  style={
                    highLightRequiredFields &&
                    selectedTagKeys.indexOf(key) === -1 &&
                    requiredTagFields.indexOf(key) !== -1
                      ? { marginBottom: '1em', borderColor: '#f2711c' }
                      : { marginBottom: '1em' }
                  }
                  onClick={this.onDropdownClick}
                  onAddItem={this.onAddTag}
                  onChange={this.onTagChange}
                />
              );
            }

            return dropdown;
          })}

        {ableToUpdateTags && (
          <div style={{ width: '100%' }}>
            <Button
              basic
              color="black"
              onClick={this.onTagUpdateButtonClick}
              disabled={updating}
              loading={updating}
              style={{
                width: '90%',
                boxSizing: 'border-box',
                margin: 0,
                display: 'inline-block'
              }}
            >
              Update
            </Button>

            <Popup
              content="Clear Tags"
              trigger={
                <Button
                  icon="repeat"
                  basic
                  color="black"
                  style={{
                    width: '10%',
                    boxSizing: 'border-box',
                    margin: 0,
                    display: 'inline-block'
                  }}
                  onClick={this.onResetTagClick}
                />
              }
            />
          </div>
        )}

        {error && error.type && (
          <Message negative style={{ bottom: 0, left: 0 }}>
            <Message.Header>{error.message}</Message.Header>
          </Message>
        )}

        <Modal size="small" open={showConfirmModal}>
          <Modal.Header>Are you sure you want to add new tag?</Modal.Header>
          <Modal.Content>
            {newTagInfo && (
              <p>
                Are you sure you want to add <strong>{newTagInfo.value}</strong>
                ?
              </p>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button negative onClick={this.onRejectAddTag}>
              No
            </Button>
            <Button positive onClick={this.onConfirmAddTag}>
              Yes
            </Button>
          </Modal.Actions>
        </Modal>
      </Container>
    );
  }
}

AssetTagSelection.defaultProps = {
  allowAdditions: false,
  ableToUpdateTags: false,
  style: {},
  tagsUpdate: null,
  updateSelectedTagOptions: null,
  editing: false,
  selectedAssets: [],
  updateAssetTags: null,
  containerComponent: 'UPLOADER', // UPLOADER, FILTER_PANEL, ASSET_PREVIEW
  onTagAdd: null,
  highLightRequiredFields: false,
  requiredTagFields: [],
  loadingOptions: false,
  user: null,
  resetSelectTagOptions: null
};

AssetTagSelection.propTypes = {
  tagOptions: PropTypes.object.isRequired,
  ableToUpdateTags: PropTypes.bool,
  allowAdditions: PropTypes.bool,
  updateTagOptions: PropTypes.func.isRequired,
  style: PropTypes.object,
  updateSelectedTagOptions: PropTypes.func,
  updateAssetTags: PropTypes.func,
  editing: PropTypes.bool,
  selectedAssets: PropTypes.arrayOf(PropTypes.object),
  containerComponent: PropTypes.string,
  onTagAdd: PropTypes.func,
  highLightRequiredFields: PropTypes.bool,
  requiredTagFields: PropTypes.arrayOf(PropTypes.string),
  loadingOptions: PropTypes.bool,
  user: PropTypes.object,
  displayError: PropTypes.func.isRequired,
  resetSelectTagOptions: PropTypes.func
};

export default AssetTagSelection;



// WEBPACK FOOTER //
// ./src/components/common/AssetGalleryV2/AssetTagSelection.jsx